import { useRef, useState } from 'react';
import api from 'api';
import { getResizedImage } from 'shared-helpers/common';
import { useDispatch, useReduxState } from 'hooks';
import { actions } from 'store';
import { waitForImage } from 'lib/files';
import { FlexContainer, ModalButtonWithForm, User } from 'components/common';
import { Icon } from 'components/common/icons';
import { ImageUploader } from 'components/common/form';

import type { RequestUser } from 'shared-types';
import type { PageState } from 'types';

const ProfileAvatarButton = ({ user, theme }) => (
  <div className="avatar-user">
    <User showNameOrUsername={false} size="huge" user={user} />

    <div className="avatar-edit-icon">
      <Icon icon="pen" />
    </div>

    <style jsx>{`
      .avatar-user {
        position: relative;
        transition: opacity 0.3s;
      }
      .avatar-user:hover {
        opacity: 0.7;
      }
      .avatar-edit-icon {
        background-color: ${theme.lightGreyDark};
        border-radius: 20px;
        font-size: 0.9rem;
        padding: 2px 0;
        position: absolute;
        text-align: center;
        top: 0px;
        right: -2px;
        width: 18px;
        height: 18px;
      }
    `}</style>
  </div>
);

interface Props {
  isPreview: boolean;
  isUserPage?: boolean;
  targetUser: RequestUser;
}

const ProfileAvatar = ({ isPreview, isUserPage, targetUser }: Props) => {
  const { dispatch } = useDispatch();
  const { messages, theme, user: loggedInUser } = useReduxState();

  const loggedInUserHasEditPermissions =
    loggedInUser?.roles.includes('superadmin') ||
    (loggedInUser?.roles.includes('admin') &&
      loggedInUser?.permissions.includes('manage-users'));

  const [avatarPreview, setAvatarPreview] = useState(null);

  const avatarUploader = useRef<any>(undefined);

  const setAvatarUploader = (imageUploader) =>
    (avatarUploader.current = imageUploader);

  const handleAvatarRemoved = () => {
    setAvatarPreview(null);
  };

  const handleAvatarThumbnailGenerated = (previewImage) => {
    setAvatarPreview(previewImage);
  };

  const handleSubmit = async (values: { avatar: string | null }) => {
    let avatar = values.avatar;

    // upload avatar
    if (avatarUploader.current.state.imageAdded) {
      const file = await avatarUploader.current.upload();
      if (file) {
        avatar = file.url;

        // wait for resized image to be available
        await waitForImage(getResizedImage({ size: 480, url: file.url }));
      }
    }
    if (!avatar) avatar = null;

    // call api
    const response = isUserPage
      ? await api.put(`/admin/users/${targetUser.id}`, { avatar })
      : await api.post('/auth/profile', { avatar });

    // handle response
    if (response.data && response.data.success) {
      if (!isUserPage) dispatch.setUser({ ...targetUser, avatar });
      dispatch.updatePageState({
        user: { ...targetUser, avatar } as PageState['user']
      });
      dispatch.addNotice({
        text: messages.profileEditProfileSuccess,
        type: 'success'
      });
    } else {
      throw new Error('error');
    }
  };

  return (
    <div className="avatar">
      {!isPreview &&
      (loggedInUserHasEditPermissions || !isUserPage) &&
      !targetUser?.roles?.includes('guest') ? (
        <ModalButtonWithForm
          buttonCategory="transparent"
          buttonProps={{ tooltip: messages.usersEditProfilePicture }}
          buttonStyle={{ backgroundColor: 'transparent', padding: 0 }}
          buttonText={<ProfileAvatarButton user={targetUser} theme={theme} />}
          initialValues={{ avatar: targetUser.avatar }}
          modalActionCategory="success"
          modalActionText={messages.commonSave}
          modalTitle={messages.usersEditProfilePicture}
          onSubmit={handleSubmit}
        >
          {(formState) => (
            <FlexContainer style={{ gap: 10, marginTop: 5 }}>
              <User
                showNameOrUsername={false}
                size="huge"
                user={{
                  ...targetUser,
                  avatar: avatarPreview || formState.values?.avatar
                }}
              />
              <ImageUploader
                field="avatar"
                id="profile-avatar"
                onFileRemoved={handleAvatarRemoved}
                onThumbnailGenerated={handleAvatarThumbnailGenerated}
                maxWidth="32px"
                messages={messages}
                setUploader={setAvatarUploader}
                showPreviewImage={false}
              />
            </FlexContainer>
          )}
        </ModalButtonWithForm>
      ) : (
        <User showNameOrUsername={false} size="huge" user={targetUser} />
      )}
    </div>
  );
};

export default ProfileAvatar;
